'use client';

import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { cn } from '@/lib/utils';
import { resolveI18nText, resolveI18nRichtext } from '@/sanity/globals/I18n';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  IPricingSectionV2,
  IPricingSpecDefinition,
  IPricingTierFee,
} from '@/sanity/lib/definitions';
import { IPricingV2Discount } from '@/sanity/templates';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shadcn/ui/select';
import {
  applyDiscountToPrice,
  FlatPlan,
  flattenPlans,
  getSpecDefinition,
  planCta,
  planFee,
  planSpecValue,
  resolveTierDiscountPercent,
  tierCtaToGroqButton,
} from './helpers';

const ease = [0.32, 0.72, 0, 1] as const;
const DEFAULT_SLOT_COUNT = 3;

type Slot =
  | { type: 'empty' }
  // `planId` rather than a tier key: tiers with a specs toggle contribute two
  // selectable plans that share the same tier.
  | { type: 'editing'; assetKey?: string; categoryKey?: string; planId?: string }
  | { type: 'filled'; planId: string };

export default function PricingComparisonBlock({
  data,
  language,
  filter,
  discount,
}: {
  data: IPricingSectionV2;
  language?: string;
  filter?: { assetKey?: string };
  discount?: IPricingV2Discount;
}) {
  const comparison = data.comparison;
  const plans = useMemo(() => {
    const all = flattenPlans(data, { splitToggleVariants: true });
    return filter?.assetKey ? all.filter((p) => p.assetKey === filter.assetKey) : all;
  }, [data, filter?.assetKey]);

  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState<Slot[]>(() =>
    Array.from({ length: DEFAULT_SLOT_COUNT }, () => ({ type: 'empty' as const })),
  );

  const showLabel = resolveI18nText(comparison?.showLabel, comparison?.showLabelI18n, language) ?? 'Compare plans';
  const hideLabel = resolveI18nText(comparison?.hideLabel, comparison?.hideLabelI18n, language) ?? 'Hide comparison';
  const addPlanLabel = resolveI18nText(comparison?.addPlanLabel, comparison?.addPlanLabelI18n, language) ?? 'Add plan';
  const productPlaceholder = resolveI18nText(comparison?.productPlaceholder, comparison?.productPlaceholderI18n, language) ?? 'Product';
  const accountSizePlaceholder = resolveI18nText(comparison?.accountSizePlaceholder, comparison?.accountSizePlaceholderI18n, language) ?? 'Account size';
  const emptyValue = resolveI18nText(comparison?.emptyValueLabel, comparison?.emptyValueLabelI18n, language) ?? '—';
  const highlightHint = resolveI18nText(comparison?.highlightHint, comparison?.highlightHintI18n, language);
  const content = resolveI18nRichtext(comparison?.content, comparison?.contentI18n, language);

  const rowKeys =
    comparison?.rowKeys?.filter(Boolean) ??
    (data.specsCatalog?.map((s) => s.key).filter(Boolean) as string[]) ??
    [];

  const filledPlans = useMemo(
    () =>
      slots
        .map((s) => (s.type === 'filled' ? plans.find((p) => p.planId === s.planId) : undefined))
        .filter((p): p is FlatPlan => !!p),
    [slots, plans],
  );

  const diffRows = useMemo(() => {
    if (filledPlans.length < 2) return new Set<string>();
    const diff = new Set<string>();
    for (const key of rowKeys) {
      const values = filledPlans.map((p) => rowValueSignature(p, key, language));
      const distinct = new Set(values);
      if (distinct.size >= 2) diff.add(key);
    }
    return diff;
  }, [filledPlans, rowKeys, language]);

  const updateSlot = (idx: number, next: Slot) =>
    setSlots((prev) => prev.map((s, i) => (i === idx ? next : s)));

  return (
    <section className="w-full flex flex-col gap-[1.25rem]">
      <Header
        content={content}
        open={open}
        showLabel={showLabel}
        hideLabel={hideLabel}
        onToggle={() => setOpen((o) => !o)}
      />

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="table"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease }}
            className="overflow-hidden"
          >
            <div className="w-full flex flex-col gap-[1rem]">
              {highlightHint && (
                <p className="mb-0 px-[1rem] text-[1.5rem] md:text-[1.75rem] leading-[1.4] font-geist italic font-normal text-black/60">
                  {highlightHint}
                </p>
              )}
              <div className="w-full overflow-x-auto rounded-[0.5rem]">
                <div className="flex items-stretch min-w-[104rem] md:min-w-0 md:w-full rounded-[0.5rem] overflow-hidden">
                  <LabelsColumn
                    rowKeys={rowKeys}
                    specsCatalog={data.specsCatalog}
                    language={language}
                    diffRows={diffRows}
                  />
                  <div className="flex flex-1 items-stretch">
                    {slots.map((slot, idx) => (
                      <PlanColumn
                        key={idx}
                        slot={slot}
                        columnIndex={idx}
                        onChange={(next) => updateSlot(idx, next)}
                        rowKeys={rowKeys}
                        plans={plans}
                        emptyValue={emptyValue}
                        addPlanLabel={addPlanLabel}
                        productPlaceholder={productPlaceholder}
                        accountSizePlaceholder={accountSizePlaceholder}
                        language={language}
                        isLast={idx === slots.length - 1}
                        diffRows={diffRows}
                        discount={discount}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

/* ─────────────────────────────────────────────── Header (node 4493-381) */

function Header({
  content,
  open,
  showLabel,
  hideLabel,
  onToggle,
}: {
  content: ReturnType<typeof resolveI18nRichtext>;
  open: boolean;
  showLabel: string;
  hideLabel: string;
  onToggle: () => void;
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-[2rem] md:gap-[1.25rem] rounded-[0.5rem] bg-[#f4f4f4] px-[2.5rem] py-[2.75rem] md:px-[4rem] md:py-[3.5rem]">
      <div className="flex-1 min-w-0">
        {content && (
          <div className="richtext richtext-start flex flex-col gap-[1rem]">
            <PortableText value={content} components={richTextComponent} />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center justify-center md:justify-start gap-[1.25rem] h-[5.25rem] md:h-[5.875rem] px-[3rem] md:pl-[4.25rem] md:pr-[3.5rem] rounded-[0.25rem] bg-black text-white text-[1.75rem] leading-[1.35] tracking-[-0.035rem] font-geist font-semibold hover:bg-black/90 transition-colors shrink-0 w-full md:w-auto"
      >
        {open ? hideLabel : showLabel}
        <ChevronIcon isOpen={open} />
      </button>
    </div>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <motion.svg
      viewBox="0 0 16 16"
      className="w-[2rem] h-[2rem] shrink-0"
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.28, ease }}
      aria-hidden="true"
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );
}

/* ─────────────────────────────────────────────── Left labels column */

function LabelsColumn({
  rowKeys,
  specsCatalog,
  language,
  diffRows,
}: {
  rowKeys: string[];
  specsCatalog?: IPricingSpecDefinition[];
  language?: string;
  diffRows: Set<string>;
}) {
  return (
    <div
      className="flex flex-col bg-[#f4f4f4] w-[24rem] md:w-[30.875rem] shrink-0 pt-[27.5rem] rounded-l-[0.5rem]"
      // pt-[27.5rem] = 220px per Figma — vertical alignment with the slot cards above
    >
      {rowKeys.map((key, idx) => {
        const def = getSpecDefinition(specsCatalog, key);
        const label = resolveI18nText(def?.label, def?.labelI18n, language);
        const isDifferent = diffRows.has(key);
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease, delay: 0.08 + idx * 0.025 }}
            className={cn(
              'flex h-[5.5rem] items-center px-[1.5rem] md:px-[2rem] w-[22rem] md:w-[27.5rem] border-b border-black/10 transition-colors',
              idx === 0 && 'border-t',
              isDifferent && 'bg-[#eefbf6]',
            )}
          >
            <p
              className={cn(
                'mb-0 text-[1.75rem] md:text-[2rem] leading-normal text-black opacity-90 font-geist',
                isDifferent ? 'font-medium' : 'font-normal',
              )}
            >
              {label}
            </p>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────── Plan column */

function PlanColumn({
  slot,
  columnIndex,
  onChange,
  rowKeys,
  plans,
  emptyValue,
  addPlanLabel,
  productPlaceholder,
  accountSizePlaceholder,
  language,
  isLast,
  diffRows,
  discount,
}: {
  slot: Slot;
  columnIndex: number;
  onChange: (next: Slot) => void;
  rowKeys: string[];
  plans: FlatPlan[];
  emptyValue: string;
  addPlanLabel: string;
  productPlaceholder: string;
  accountSizePlaceholder: string;
  language?: string;
  isLast: boolean;
  diffRows: Set<string>;
  discount?: IPricingV2Discount;
}) {
  const filledPlan =
    slot.type === 'filled' ? plans.find((p) => p.planId === slot.planId) : undefined;
  const filledPlanCta = filledPlan ? planCta(filledPlan) : undefined;

  const columnDelay = 0.1 + columnIndex * 0.06;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease, delay: columnDelay }}
      className={cn(
        'flex flex-[1_0_0] min-w-0 flex-col gap-[1.375rem] bg-[#f4f4f4] py-[0.75rem] px-[1.125rem]',
        isLast && 'rounded-r-[0.5rem]',
      )}
    >
      {/* Top card slot (h-[204px] = 25.5rem) */}
      <div className="h-[25.5rem] flex flex-col relative">
        <AnimatePresence mode="wait" initial={false}>
          {slot.type === 'empty' && (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22, ease }}
              className="flex flex-1 flex-col"
            >
              <EmptyPlaceholder
                onClick={() => onChange({ type: 'editing' })}
                label={addPlanLabel}
              />
            </motion.div>
          )}
          {slot.type === 'editing' && (
            <motion.div
              key="editing"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22, ease }}
              className="flex flex-1 flex-col"
            >
              <PlanForm
                slot={slot}
                plans={plans}
                language={language}
                productPlaceholder={productPlaceholder}
                accountSizePlaceholder={accountSizePlaceholder}
                onCancel={() => onChange({ type: 'empty' })}
                onConfirm={(planId) => onChange({ type: 'filled', planId })}
                onChange={(next) => onChange(next)}
              />
            </motion.div>
          )}
          {slot.type === 'filled' && filledPlan && (
            <motion.div
              key="filled"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22, ease }}
              className="flex flex-1 flex-col"
            >
              <ActivePlanCard
                plan={filledPlan}
                language={language}
                onRemove={() => onChange({ type: 'empty' })}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Data rows */}
      <div className="flex flex-col w-full">
        {rowKeys.map((key, idx) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease, delay: columnDelay + 0.05 + idx * 0.025 }}
          >
            <PlanRow
              rowKey={key}
              plan={filledPlan}
              emptyValue={emptyValue}
              language={language}
              isFirst={idx === 0}
              isDifferent={diffRows.has(key)}
              discount={discount}
            />
          </motion.div>
        ))}
      </div>

      {/* Bottom CTA */}
      <AnimatePresence initial={false}>
        {filledPlanCta && (
          <motion.div
            key="cta"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.28, ease }}
            className="w-full"
          >
            <CTAButton
              data={tierCtaToGroqButton(
                filledPlanCta,
                resolveI18nText(filledPlanCta.title, filledPlanCta.titleI18n, language),
              )!}
              className="w-full"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────── Slot: empty */

function EmptyPlaceholder({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-h-[10rem] rounded-[0.5rem] border border-dashed border-[#228e71] bg-white flex flex-col items-center justify-center gap-[0.5rem] hover:bg-[#228e71]/[0.04] transition-colors"
    >
      <span className="inline-flex items-center justify-center p-[0.5rem]">
        <svg viewBox="0 0 22 22" fill="none" className="w-[2.75rem] h-[2.75rem]" aria-hidden="true">
          <path
            d="M11 5V17M5 11H17"
            stroke="#228e71"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <span className="text-[2rem] leading-normal text-black font-geist font-normal">
        {label}
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────── Slot: editing (form) */

function PlanForm({
  slot,
  plans,
  language,
  productPlaceholder,
  accountSizePlaceholder,
  onCancel,
  onConfirm,
  onChange,
}: {
  slot: Extract<Slot, { type: 'editing' }>;
  plans: FlatPlan[];
  language?: string;
  productPlaceholder: string;
  accountSizePlaceholder: string;
  onCancel: () => void;
  onConfirm: (planId: string) => void;
  onChange: (next: Slot) => void;
}) {
  const assetOptions = useMemo(() => {
    const seen = new Map<string, string>();
    plans.forEach((p) => {
      if (p.assetKey && !seen.has(p.assetKey)) seen.set(p.assetKey, p.assetLabel);
    });
    return Array.from(seen, ([key, label]) => ({ key, label }));
  }, [plans]);

  const categoryOptions = useMemo(() => {
    if (!slot.assetKey) return [];
    const seen = new Map<string, string>();
    plans
      .filter((p) => p.assetKey === slot.assetKey)
      .forEach((p) => {
        if (p.categoryKey && !seen.has(p.categoryKey)) {
          const sub = p.categorySubLabel ? ` · ${p.categorySubLabel}` : '';
          seen.set(p.categoryKey, `${p.categoryName}${sub}`);
        }
      });
    return Array.from(seen, ([key, label]) => ({ key, label }));
  }, [plans, slot.assetKey]);

  // Tiers with a specs toggle contribute one option per tab, labelled
  // "$50k · Daily Loss Limit", so each product can be compared on its own.
  const tierOptions = useMemo(() => {
    if (!slot.assetKey || !slot.categoryKey) return [];
    return plans
      .filter((p) => p.assetKey === slot.assetKey && p.categoryKey === slot.categoryKey)
      .map((p) => {
        const amount =
          resolveI18nText(p.tier.amount, p.tier.amountI18n, language) ?? p.tier.key ?? '';
        const variant = resolveI18nText(p.variantLabel, p.variantLabelI18n, language);
        return {
          key: p.planId,
          label: variant ? `${amount} · ${variant}` : amount,
        };
      });
  }, [plans, slot.assetKey, slot.categoryKey, language]);

  const canConfirm = !!slot.assetKey && !!slot.categoryKey && !!slot.planId;
  const matchedPlan = plans.find((p) => p.planId === slot.planId);

  return (
    <div className="flex-1 rounded-[0.5rem] border border-black/10 bg-white p-[2rem] flex flex-col gap-[1rem]">
      <Dropdown
        placeholder="CFD + Crypto"
        value={slot.assetKey}
        options={assetOptions}
        onChange={(assetKey) => onChange({ type: 'editing', assetKey })}
      />
      <Dropdown
        placeholder={productPlaceholder}
        value={slot.categoryKey}
        options={categoryOptions}
        disabled={!slot.assetKey}
        onChange={(categoryKey) =>
          onChange({ type: 'editing', assetKey: slot.assetKey, categoryKey })
        }
      />
      <div className="rounded-[0.75rem] bg-[#228e71]/10 p-[0.25rem]">
        <Dropdown
          placeholder={accountSizePlaceholder}
          value={slot.planId}
          options={tierOptions}
          disabled={!slot.categoryKey}
          highlightBorder
          onChange={(planId) =>
            onChange({
              type: 'editing',
              assetKey: slot.assetKey,
              categoryKey: slot.categoryKey,
              planId,
            })
          }
        />
      </div>
      <div className="flex items-center gap-[1rem] pt-[0.25rem]">
        <button
          type="button"
          disabled={!canConfirm}
          onClick={() => matchedPlan && onConfirm(matchedPlan.planId)}
          className="flex-1 h-[4.25rem] rounded-[0.25rem] bg-[#1aa87f] text-white text-[1.625rem] leading-normal font-geist font-medium hover:bg-[#1aa87f]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-[4.25rem] rounded-[0.25rem] border border-[#e5e7eb] bg-white text-[#1f2937] text-[1.625rem] leading-normal font-geist font-medium hover:bg-black/[0.02] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Dropdown({
  value,
  options,
  placeholder,
  onChange,
  disabled,
  highlightBorder,
}: {
  value?: string;
  options: { key: string; label: string }[];
  placeholder: string;
  onChange: (key: string) => void;
  disabled?: boolean;
  highlightBorder?: boolean;
}) {
  return (
    <Select
      value={value ?? undefined}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          'w-full h-[4.75rem] rounded-[0.5rem] border bg-white px-[1.5rem] text-[2rem] leading-normal font-geist font-normal text-black opacity-90 focus:ring-0 focus:ring-offset-0 shadow-none data-[placeholder]:text-black/50 [&>svg]:w-[2rem] [&>svg]:h-[2rem] [&>svg]:opacity-60',
          highlightBorder ? 'border-[#228e71]/40' : 'border-[#d1d5db]',
          disabled && 'opacity-40',
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-[0.5rem] border border-black/10 bg-white text-black">
        {options.map((opt) => (
          <SelectItem
            key={opt.key}
            value={opt.key}
            className="text-[2rem] leading-normal font-geist py-[1rem] pr-[2rem] pl-[3rem] cursor-pointer focus:bg-[#228e71]/10 focus:text-black"
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ─────────────────────────────────────────────── Slot: filled (active plan) */

function ActivePlanCard({
  plan,
  language,
  onRemove,
}: {
  plan: FlatPlan;
  language?: string;
  onRemove: () => void;
}) {
  const amount = resolveI18nText(plan.tier.amount, plan.tier.amountI18n, language);
  const subLabel = plan.categorySubLabel;
  // The toggle variant is what distinguishes two otherwise identical columns,
  // so it has to be visible in the header.
  const variant = resolveI18nText(plan.variantLabel, plan.variantLabelI18n, language);
  const subtitle = [subLabel, plan.assetLabel, variant].filter(Boolean).join(' · ');

  return (
    <div className="relative flex-1 rounded-[0.5rem] border border-black/10 bg-white p-[2rem] flex flex-col items-start justify-center gap-[1rem]">
      <p className="mb-0 w-full pr-[3.5rem] text-[3rem] leading-normal tracking-[-0.09rem] font-geist font-medium text-black">
        {plan.categoryName} {amount}
      </p>
      {subtitle && (
        <p className="mb-0 text-[2rem] leading-normal text-black opacity-[0.62] font-geist font-normal">
          {subtitle}
        </p>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-[1.125rem] right-[1.125rem] inline-flex items-center justify-center w-[2.75rem] h-[2.75rem] text-black/40 hover:text-black transition-colors"
        aria-label="Remove plan"
      >
        <svg viewBox="0 0 22 22" className="w-full h-full" fill="none" aria-hidden="true">
          <path
            d="M6 6L16 16M16 6L6 16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────── Data row */

function PlanRow({
  rowKey,
  plan,
  emptyValue,
  language,
  isFirst,
  isDifferent,
  discount,
}: {
  rowKey: string;
  plan: FlatPlan | undefined;
  emptyValue: string;
  language?: string;
  isFirst: boolean;
  isDifferent: boolean;
  discount?: IPricingV2Discount;
}) {
  const spec = plan ? planSpecValue(plan, rowKey) : undefined;
  const value = resolveI18nText(spec?.value, spec?.valueI18n, language);
  const isOneTimeFee = rowKey === 'oneTimeFee';

  return (
    <div
      className={cn(
        'flex items-center px-[3rem] w-full bg-white border-b border-black/10 transition-colors h-[5.5rem]',
        isFirst && 'border-t',
        isDifferent && 'bg-[#eefbf6]',
      )}
    >
      {plan && isOneTimeFee && planFee(plan) ? (
        <FeeCell plan={plan} language={language} discount={discount} />
      ) : plan && value ? (
        <p
          className={cn(
            'mb-0 text-[2rem] leading-[1.5] font-geist text-black whitespace-nowrap',
            isDifferent ? 'font-semibold opacity-100' : 'font-medium opacity-80',
          )}
        >
          {value}
        </p>
      ) : (
        <p className="mb-0 text-[1.75rem] leading-normal font-inter font-normal text-[#9ca3af] whitespace-nowrap">
          {plan ? emptyValue : '-'}
        </p>
      )}
    </div>
  );
}

function FeeCell({
  plan,
  language,
  discount,
}: {
  plan: FlatPlan;
  language?: string;
  discount?: IPricingV2Discount;
}) {
  // One price per column: the toggle variants are separate plans now, and each
  // column already names its variant in the header.
  const discountPercent = resolveTierDiscountPercent(plan.assetKey, plan.categoryKey, discount);

  return <FeeInline fee={planFee(plan)} language={language} discountPercent={discountPercent} />;
}

function FeeInline({
  fee,
  language,
  discountPercent,
}: {
  fee?: IPricingTierFee;
  language?: string;
  discountPercent?: number;
}) {
  const rawCurrent = resolveI18nText(fee?.current, fee?.currentI18n, language);
  const rawOriginal = resolveI18nText(fee?.original, fee?.originalI18n, language);
  const rawDiscountLabel = resolveI18nText(fee?.discountLabel, fee?.discountLabelI18n, language);

  const usingGlobalDiscount = !!discountPercent && !!rawOriginal;
  const original = rawOriginal;
  const current = usingGlobalDiscount
    ? applyDiscountToPrice(rawOriginal, discountPercent!)
    : rawCurrent;
  const discount = usingGlobalDiscount ? `-${discountPercent}%` : rawDiscountLabel;
  return (
    <div className="flex items-center gap-[1rem] flex-wrap">
      <div className="flex items-center gap-[0.5rem]">
        {original && (
          <span className="text-[1.75rem] leading-[1.35] tracking-[-0.0525rem] font-geist font-medium text-black line-through opacity-[0.43] whitespace-nowrap">
            {original}
          </span>
        )}
        {current && (
          <span className="text-[2rem] leading-[1.5] font-geist font-medium text-black opacity-80 whitespace-nowrap">
            {current}
          </span>
        )}
      </div>
      {discount && (
        <span className="inline-flex items-center justify-center px-[1rem] py-[0.5rem] rounded-[3.125rem] bg-[#1aa87f] text-white text-[1.5rem] leading-[1.35] font-geist font-medium whitespace-nowrap">
          {discount}
        </span>
      )}
    </div>
  );
}

// Must mirror what PlanRow renders, or the "rows that differ" highlight lies.
function rowValueSignature(plan: FlatPlan, rowKey: string, language?: string): string {
  if (rowKey === 'oneTimeFee') {
    const fee = planFee(plan);
    return resolveI18nText(fee?.current, fee?.currentI18n, language) ?? '';
  }
  const spec = planSpecValue(plan, rowKey);
  return resolveI18nText(spec?.value, spec?.valueI18n, language) ?? '';
}
